import React, { useState } from 'react';
import styled from 'styled-components';
import { Popover, Text } from '@alefeducation/ui-elements-library-core';
import { SignOutAlt } from '@alefeducation/ui-elements-library-icons';
import UserAvatar from './UserAvatar';
import { formatRole, getInitials } from './utils';
import appLocalStorage from '../../utils/AppLocalStorage';
import { clearAuthSession } from '../../utils/authUtils';
import { LOGOUT, PROFILE_MENU } from '../../utils/constants';
import {
  NAV_MENU_COLLAPSED_WIDTH,
  NAV_MENU_EXPANDED_WIDTH,
} from '../NavigationMenu/NavigationMenu';

export const TOP_BAR_HEIGHT = 57;

const PROFILE_MENU_WIDTH = 224;

const StyledTopBar = styled.header<{ isNavCollapsed: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  left: ${({ isNavCollapsed }) =>
    isNavCollapsed ? NAV_MENU_COLLAPSED_WIDTH : NAV_MENU_EXPANDED_WIDTH}px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  box-sizing: border-box;
  height: ${TOP_BAR_HEIGHT}px;
  padding: 6px 16px 6px 24px;
  border-bottom: 1px solid ${({ theme }: any) => theme.colors.primary['10']};
  background-color: ${({ theme }: any) => theme.colors.primary['0']};
  transition: left 0.2s ease-in-out;
`;

const StyledUserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StyledUserMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  white-space: nowrap;
`;

const StyledAvatarButton = styled.button`
  border-radius: 50%;
  cursor: pointer;
`;

const StyledProfileMenu = styled.div`
  display: flex;
  flex-direction: column;
  width: ${PROFILE_MENU_WIDTH}px;
  padding: 8px 0;
  border: ${({ theme }: any) => theme.borders.default};
  border-radius: ${({ theme }: any) => theme.radii.large}px;
  background-color: ${({ theme }: any) => theme.colors.primary['0']};
  box-shadow: 0 22px 27px rgba(72, 84, 101, 0.2);
`;

const StyledProfileMenuItem = styled.a`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px 12px 24px;
  border-radius: ${({ theme }: any) => theme.radii.default}px;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    background-color: ${({ theme }: any) => theme.colors.interactive['20']};
  }

  svg {
    flex-shrink: 0;
  }
`;

interface TopBarProps {
  isNavCollapsed?: boolean;
}

const TopBar = ({ isNavCollapsed = false }: TopBarProps) => {
  const user = appLocalStorage.getAlefUser()?.user;
  const initials = getInitials(user?.firstName, user?.lastName, user?.email);
  const [avatarRef, setAvatarRef] = useState<HTMLButtonElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  return (
    <StyledTopBar data-testid="top-bar" isNavCollapsed={isNavCollapsed}>
      <StyledUserProfile>
        <StyledUserMeta>
          <Text variant="body-large" weight="bold" color="text100">
            {formatRole(user?.role)}
          </Text>
          <Text variant="body-medium" weight="light" color="primary.100">
            {user?.email ?? ''}
          </Text>
        </StyledUserMeta>
        <StyledAvatarButton
          type="button"
          ref={setAvatarRef}
          aria-label={PROFILE_MENU}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <UserAvatar initials={initials} />
        </StyledAvatarButton>
      </StyledUserProfile>
      {avatarRef && (
        <Popover
          anchorRef={avatarRef}
          isOpen={isMenuOpen}
          setIsOpen={setIsMenuOpen}
          popoverOffset={4}
        >
          <StyledProfileMenu role="menu" data-testid="profile-menu">
            <StyledProfileMenuItem
              href="/logout"
              role="menuitem"
              data-testid="logout"
              onClick={() => {
                clearAuthSession();
              }}
            >
              <SignOutAlt color="primary.100" width={18} height={18} />
              <Text variant="body-large" weight="light" color="primary.100">
                {LOGOUT}
              </Text>
            </StyledProfileMenuItem>
          </StyledProfileMenu>
        </Popover>
      )}
    </StyledTopBar>
  );
};

export default TopBar;
